import { useState, type FormEvent } from "react";
import SectionHeading from "./SectionHeading";

const SUBMIT_URL = "https://readdy.ai/api/form/dae3j5rep053ijub1850";

type Status = "idle" | "sending" | "success" | "error";

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [charCount, setCharCount] = useState(0);

  const processTips = [
    "尽量写明话题的来龙去脉，线索越清楚整理越快",
    "附上可公开核验的信源链接，采纳率会大幅提升",
    "涉及未经证实的内容，我们会先标注“发酵中”再跟进",
    "你的投稿默认不会公开展示个人信息，请放心提交",
  ];

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    // 反垃圾蜜罐校验
    const honeypot = (fd.get("website_alt") as string | null)?.trim() ?? "";
    if (honeypot) {
      setStatus("success");
      form.reset();
      setCharCount(0);
      return;
    }
    // 空蜜罐不入参
    fd.delete("website_alt");

    setStatus("sending");
    setFormError("");
    try {
      const res = await fetch(SUBMIT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const serverMsg =
        (meta?.message as string) || (parsed?.message as string) || (meta?.detail as string) || responseText;
      const okCode = parsed?.code === "OK";
      if (res.ok && okCode) {
        setStatus("success");
        form.reset();
        setCharCount(0);
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请稍后再试。");
    }
  };

  const inputCls =
    "w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-100";

  return (
    <section id="contact" className="scroll-mt-16 bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading
          eyebrow="投稿 · 纠错 · 联系"
          title="提供线索，一起把瓜吃明白"
          highlight="欢迎投稿"
          desc="有刚热起来的线索、想纠错的榜单条目，或想对频道提建议？通过下方表单发给我们，编辑部会尽快处理。"
        />

        <div className="mt-12 grid grid-cols-1 gap-6 lg:grid-cols-[400px_1fr]">
          {/* left info */}
          <div className="flex flex-col gap-4">
            <div className="overflow-hidden rounded-2xl bg-foreground-950 p-6 text-background-50">
              <h3 className="font-serif text-xl font-bold">投稿小贴士</h3>
              <ul className="mt-5 flex flex-col gap-3">
                {processTips.map((tip, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-background-100">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-500 font-display text-[11px] text-foreground-950">
                      {i + 1}
                    </span>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-background-200 bg-background-100/60 p-6">
              <h3 className="font-serif text-lg font-bold text-foreground-950">其他联系方式</h3>
              <div className="mt-4 flex flex-col gap-3 text-sm text-foreground-700">
                <a href="mailto:hello@jiukanliao.example" className="flex items-center gap-3 hover:text-primary-600 cursor-pointer transition-colors">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-200 text-foreground-800">
                    <i className="ri-mail-line w-4 h-4 flex items-center justify-center"></i>
                  </span>
                  hello@jiukanliao.example
                </a>
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-200 text-foreground-800">
                    <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                  </span>
                  工作日 10:00 - 18:00 回复
                </div>
              </div>
            </div>
          </div>

          {/* form */}
          <form
            data-readdy-form
            name="jiukanliao-submit"
            onSubmit={handleSubmit}
            className="rounded-3xl border border-background-200 bg-background-100/50 p-6 md:p-8"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <label htmlFor="title" className="mb-1.5 block text-sm font-medium text-foreground-900">
                  话题 / 线索标题 <span className="text-primary-600">*</span>
                </label>
                <input id="title" name="title" required placeholder="例：某综艺名场面两派吵翻了" className={inputCls} />
              </div>
              <div>
                <label htmlFor="category" className="mb-1.5 block text-sm font-medium text-foreground-900">
                  所属栏目
                </label>
                <select id="category" name="category" defaultValue="娱乐饭圈" className={inputCls}>
                  <option>娱乐饭圈</option>
                  <option>综艺影视</option>
                  <option>电竞游戏</option>
                  <option>科技数码</option>
                  <option>职场社会</option>
                  <option>生活八卦</option>
                  <option>其他</option>
                </select>
              </div>
              <div>
                <label htmlFor="source" className="mb-1.5 block text-sm font-medium text-foreground-900">
                  公开信源链接
                </label>
                <input id="source" name="source" placeholder="https://...（可公开核验）" className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="content" className="mb-1.5 block text-sm font-medium text-foreground-900">
                  简要经过 <span className="text-primary-600">*</span>
                </label>
                <textarea
                  id="content"
                  name="content"
                  required
                  rows={5}
                  maxLength={500}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  placeholder="请用一两段话写清楚事件经过、当前进展和你掌握的信源……"
                  className={`${inputCls} resize-none`}
                ></textarea>
                <div className="mt-1 text-right text-xs text-foreground-500">{charCount}/500</div>
              </div>
              <div>
                <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-900">
                  你的称呼
                </label>
                <input id="name" name="name" placeholder="怎么称呼你" className={inputCls} />
              </div>
              <div>
                <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-900">
                  联系方式 <span className="text-primary-600">*</span>
                </label>
                <input id="email" name="email" type="email" required placeholder="用于接收处理结果" className={inputCls} />
              </div>

              {/* honeypot */}
              <div className="sm:col-span-2">
                <input
                  name="website_alt"
                  type="text"
                  autoComplete="off"
                  tabIndex={-1}
                  aria-hidden="true"
                  readOnly
                />
              </div>
            </div>

            {status === "error" && formError && (
              <p className="mt-4 flex items-start gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-800">
                <i className="ri-error-warning-fill mt-0.5 w-4 h-4 flex items-center justify-center shrink-0"></i>
                {formError}
              </p>
            )}
            {status === "success" && (
              <p className="mt-4 flex items-start gap-2 rounded-lg border border-background-200 bg-background-100 px-4 py-3 text-sm text-foreground-800">
                <i className="ri-checkbox-circle-fill mt-0.5 w-4 h-4 flex items-center justify-center shrink-0 text-primary-600"></i>
                收到你的投稿啦！编辑部会核对公开信源后尽快处理，结果将发送到你填写的邮箱。
              </p>
            )}

            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-foreground-500">提交即表示你知晓：我们仅做公开信息整理，不代你下结论。</p>
              <button
                type="submit"
                disabled={status === "sending"}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-white transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
              >
                {status === "sending" ? (
                  <>
                    <i className="ri-loader-4-line w-4 h-4 flex items-center justify-center animate-spin"></i>
                    提交中…
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill w-4 h-4 flex items-center justify-center"></i>
                    提交线索
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}